<template>
    <div>
        <div class="panel uk-container uk-padding">
            <header uk-grid>
                <div class="uk-width-2-3@m">
                    <h2 class="uk-margin-bottom">
                        Export payments
                    </h2>
                </div>
            </header>
            <div class="datepicker-trigger">
                <form-wrapper
                    class="uk-form-stacked uk-grid-small"
                    :validator="$v.form"
                    uk-grid
                >
                    <div class="uk-width-1-2">
                        <datetime-field-improved
                            v-model="form.startDate"
                            icon="calendar"
                            class=""
                            placeholder="Select Start Date"
                            name="startDate"
                            :v="$v.form.startDate"
                            :label="'Start Date'"
                            zone="local"
                            value-zone="local"
                            format="dd/MM/yyyy"
                            :show-error-on-blur-only="true"
                            @input="onStartDateChange"
                        />
                    </div>
                    <div class="uk-width-1-2">
                        <datetime-field-improved
                            v-model="form.endDate"
                            icon="calendar"
                            class=""
                            placeholder="Select End Date"
                            name="endDate"
                            :v="$v.form.endDate"
                            zone="local"
                            value-zone="local"
                            format="dd/MM/yyyy"
                            :label="'End Date'"
                            :show-error-on-blur-only="true"
                            @input="onEndDateChange"
                        />
                    </div>
                    <div class="uk-width-1-2">
                        <select-field
                            v-model="form.filters.payment_type"
                            placeholder="Any"
                            name="payment_type"
                            :options="sortedPaymentTypes"
                            label="Payment Type"
                            :show-error-on-blur-only="true"
                            :enable-null-option="true"
                        />
                    </div>
                    <div class="uk-width-1-2">
                        <select-field
                            v-model="form.filters.payment_status"
                            placeholder="Any"
                            name="payment_status"
                            :options="sortedPaymentStatuses"
                            label="Payment Status"
                            :show-error-on-blur-only="true"
                            :enable-null-option="true"
                        />
                    </div>
                    <div class="uk-width-1-2">
                        <text-field
                            v-model="form.filters.job_number"
                            placeholder="Any"
                            label="Job Number"
                            name="job_number"
                            :show-error-on-blur-only="true"
                        />
                    </div>
                    <div class="uk-width-1-2">
                        <text-field
                            v-model="form.filters.address"
                            placeholder="Any"
                            label="Address"
                            name="address"
                            :show-error-on-blur-only="true"
                        />
                    </div>
                    <div class="uk-width-1-2">
                        <text-field
                            v-model="form.filters.received_by"
                            placeholder="Any"
                            label="Received By"
                            name="received_by"
                            :show-error-on-blur-only="true"
                        />
                    </div>
                    <div class="uk-width-1-2">
                        <text-field
                            v-model="form.filters.client_name"
                            placeholder="Any"
                            label="Client Name"
                            name="client_name"
                            :show-error-on-blur-only="true"
                        />
                    </div>
                    <div class="uk-margin-large-top uk-flex uk-width-1-1">
                        <div class="uk-width-1-3 uk-flex uk-flex-bottom">
                            <button
                                class="uk-button uk-button-primary uk-width-1-1"
                                :disabled="!isFormValid"
                                @click="getReport"
                            >
                                Generate Report
                            </button>
                        </div>
                        <div class="uk-width-2-3 uk-flex uk-flex-right">
                            <button
                                class="uk-button uk-button-text"
                                @click="resetFilters"
                            >
                                Reset Filters
                            </button>
                        </div>
                    </div>
                </form-wrapper>
            </div>
        </div>
    </div>
</template>

<script>

import { required } from "vuelidate/lib/validators";
import { mapGetters } from 'vuex'
import { getPaymentFormDropdownItemsRequest, getReportLinkRequest } from '../../helpers/api'
import { deriveReportEndDate, deriveReportStartDate } from '../../helpers/helpers'
import { DateTime } from 'luxon';
import { sortByKey } from "@strangeanimals/common-vue-components/src/helpers/helpers";
const DatetimeFieldImproved = () => import("@strangeanimals/common-vue-components/src/components/fields/DatetimeFieldImproved")
const SelectField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/SelectField");
const TextField  = () => import( "@strangeanimals/common-vue-components/src/components/fields/TextField.vue");

export default {
    components: { DatetimeFieldImproved, SelectField, TextField },
    data() {
        return {
            form: {
                startDate: '',
                endDate: '',
                filters: {
                    payment_type: null,
                    payment_status: null,
                    address: null,
                    job_number: null,
                    received_by: null,
                    client_name: null
                }
            },
            paymentTypes: {},
            paymentStates: {},
            paymentMethods: {},
            receiptTypes: {},
            paymentStatuses: {}
        }
    },
    computed: {
        ...mapGetters({
            user: 'user',
            serverTimezone: 'serverTimezone'
        }),
        isFormValid() {
            return !this.$v.form.$invalid;
        },
        timezone() {
            return this.serverTimezone ? this.serverTimezone : 'local';
        },
        sortedPaymentTypes: function () {
            return sortByKey(this.paymentTypes)
        },
        sortedPaymentStatuses: function () {
            return sortByKey(this.paymentStatuses)
        },
    },
    mounted() {
        this.resetFilters();
        this.getPaymentFormDropdownItems();
    },
    validations: {
        form: {
            startDate: { required },
            endDate: { required }
        }
    },
    methods: {
        onStartDateChange(value) {
            const date1 = DateTime.fromISO(this.form.startDate, {setZone: true});
            const date2 = DateTime.fromISO(this.form.endDate, {setZone: true});
            if (date1 >= date2) {
                this.form.endDate = date1.plus({days: 1}).endOf('month').toFormat('yyyy-LL-dd');
            }
        },
        onEndDateChange(value) {
            const date1 = DateTime.fromISO(this.form.startDate, {setZone: true});
            const date2 = DateTime.fromISO(this.form.endDate, {setZone: true});
            if (date1 >= date2) {
                this.form.startDate = date2.minus({days: 1}).startOf('month').toFormat('yyyy-LL-dd');
            }
        },
        getReport(paymentIDs) {
            let self = this;
            let filters = {};
            Object.keys(self.form.filters).forEach((key) => {
                if (self.form.filters.hasOwnProperty(key)) {
                    if (self.form.filters[key] != null) {
                        filters[key] = self.form.filters[key];
                    }
                }
            });
            const data = {
                printedBy: this.user.id,
                startDate: deriveReportStartDate(DateTime.fromISO(this.form.startDate, {setZone: true}), this.timezone),
                endDate: deriveReportEndDate(DateTime.fromISO(this.form.endDate, {setZone: true}), this.timezone),
                filters: filters
            };
            getReportLinkRequest(data).then((response) => {
                if (response.body.exists) {
                    window.open(response.body.csv, '_blank');
                }
            });
        },
        getPaymentFormDropdownItems() {
            const self = this;
            getPaymentFormDropdownItemsRequest({
                billerCodeSelector: self.billerCodeSelector
            }).then((response) => {
                self.paymentTypes = response.body.paymentTypes;
                self.paymentStates = response.body.paymentStates;
                self.paymentMethods = response.body.paymentMethods;
                self.receiptTypes = response.body.receiptTypes;
                self.paymentStatuses = response.body.paymentStatuses;
            });
        },
        resetFilters() {
            this.form.startDate = DateTime.local().minus({days: 7}).toFormat('yyyy-LL-dd');
            this.form.endDate = DateTime.local().toFormat('yyyy-LL-dd');
            this.form.filters.payment_type = null;
            this.form.filters.payment_status = null;
            this.form.filters.address = null;
            this.form.filters.job_number = null;
            this.form.filters.received_by = null;
            this.form.filters.client_name = null
        }
    }
}
</script>
